import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import posthog from 'posthog-js';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import { getLevelById, getRegistrationStep } from '../../constants/accountLevels';

const compactDigits = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/\D/g, '')
    .slice(0, 11);
};

const formatCpf = (digits) => {
  if (!digits) return '';
  const onlyDigits = compactDigits(digits);
  const part1 = onlyDigits.slice(0, 3);
  const part2 = onlyDigits.slice(3, 6);
  const part3 = onlyDigits.slice(6, 9);
  const part4 = onlyDigits.slice(9, 11);
  let formatted = part1;
  if (part2) formatted += `.${part2}`;
  if (part3) formatted += `.${part3}`;
  if (part4) formatted += `-${part4}`;
  return formatted;
};

const normalizeBirthDate = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value !== 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
  }
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split('/');
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

const AccountLevelInput = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const levelId = useMemo(() => {
    const parsed = Number(level);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [level]);

  const isBirthDateValid = useMemo(() => {
    if (!birthDate) return false;
    const normalized = normalizeBirthDate(birthDate);
    if (!normalized) return false;
    
    const birthDateObj = new Date(normalized);
    if (Number.isNaN(birthDateObj.getTime())) return false;
    
    const year = birthDateObj.getFullYear();
    const yearFromString = normalized.split('-')[0];
    
    if (year < 1900 || year === 1 || yearFromString === '0001' || yearFromString === '0000' || parseInt(yearFromString, 10) < 1900) {
      return false;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    birthDateObj.setHours(0, 0, 0, 0);
    
    if (birthDateObj > today) return false;
    
    const maxAgeDate = new Date();
    maxAgeDate.setFullYear(maxAgeDate.getFullYear() - 99);
    maxAgeDate.setHours(0, 0, 0, 0);
    
    if (birthDateObj < maxAgeDate) return false;
    
    return true;
  }, [birthDate]);

  const levelConfig = useMemo(() => {
    if (!Number.isFinite(levelId)) return null;
    return getLevelById(levelId);
  }, [levelId]);

  const minBirthDate = useMemo(() => {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 99);
    return minDate.toISOString().slice(0, 10);
  }, []);

  const maxBirthDate = useMemo(() => {
    return new Date().toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [profileResponse, userResponse] = await Promise.allSettled([
          api.get(`/account/profile/${user.id}`),
          api.get(`/user/id/${user.id}`),
        ]);

        const profile = profileResponse.status === 'fulfilled' ? profileResponse.value.data?.profile || {} : {};
        const userData = userResponse.status === 'fulfilled' ? userResponse.value.data || {} : {};

        const deriveNames = (value) => {
          if (typeof value !== 'string') return { first: '', last: '' };
          const trimmed = value.trim();
          if (!trimmed) return { first: '', last: '' };
          const parts = trimmed.split(' ').filter(Boolean);
          const first = parts.shift() || '';
          const last = parts.length ? parts.join(' ') : '';
          return { first, last };
        };

        const safe = (value) => (typeof value === 'string' ? value.trim() : '');

        const profileFirst = safe(profile.firstName ?? profile.firstname ?? profile.name ?? userData.name ?? userData.firstName);
        const profileLast = safe(profile.lastName ?? profile.lastname ?? userData.lastname ?? userData.lastName ?? userData.surname);
        const profileFull = safe(profile.fullName ?? profile.fullname ?? userData.fullName);
        const profileCpf =
          compactDigits(
            profile.cpf ??
              profile.CPF ??
              profile.document ??
              profile.documentNumber ??
              profile.doc ??
              profile.taxId ??
              userData.cpf ??
              userData.CPF ??
              userData.document ??
              userData.documentNumber ??
              userData.doc ??
              userData.taxId ??
              user?.cpf ??
              user?.document ??
              user?.taxId
          ) || '';
        const profileBirth =
          normalizeBirthDate(
            profile.birthDate ??
              profile.birthdate ??
              profile.dateOfBirth ??
              profile.birthday ??
              profile.DataNascimento ??
              profile.data_nascimento ??
              userData.birthDate ??
              userData.birthdate ??
              userData.dateOfBirth ??
              userData.dateBirth ??
              userData.birthday ??
              user?.birthDate ??
              user?.dateBirth
          ) || '';
        const fallbackName = profileFull || safe(user.name) || '';

        let first = profileFirst;
        let last = profileLast;

        if (!first || !last) {
          const derived = deriveNames(fallbackName);
          if (!first) first = derived.first;
          if (!last) last = derived.last;
        }

        setFirstName(first);
        setLastName(last);
        setCpf(formatCpf(profileCpf));
        setBirthDate(profileBirth);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar seus dados para este nível.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const registrationStep = useMemo(() => getRegistrationStep(user), [user]);
  
  const isLevel2DataComplete = useMemo(() => {
    if (levelId !== 2) return false;
    // Verifica se o nível 2 já foi submetido verificando se o registrationStep é >= 2
    // Isso indica que os dados foram enviados para a API
    if (registrationStep >= 2) return true;
    return false;
  }, [levelId, registrationStep]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || !user?.id || isLevel2DataComplete) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const digitsCpf = cpf.replace(/\D/g, '');
    const normalizedBirth = birthDate ? normalizeBirthDate(birthDate) : '';

    setError('');
    setSuccess('');

    if (!trimmedFirst) {
      setError('Informe seu nome.');
      return;
    }

    if (!trimmedLast) {
      setError('Informe seu sobrenome.');
      return;
    }

    if (digitsCpf.length !== 11) {
      setError('Informe um CPF válido.');
      return;
    }

    if (!normalizedBirth) {
      setError('Informe sua data de nascimento.');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const birthDateObj = new Date(normalizedBirth);
    birthDateObj.setHours(0, 0, 0, 0);
    
    if (Number.isNaN(birthDateObj.getTime()) || birthDateObj > today) {
      setError('Informe uma data de nascimento válida.');
      return;
    }

    const year = birthDateObj.getFullYear();
    const yearFromString = normalizedBirth.split('-')[0];
    
    if (year < 1900 || year === 1 || yearFromString === '0001' || yearFromString === '0000' || parseInt(yearFromString, 10) < 1900) {
      setError('Informe um ano válido para a data de nascimento (mínimo 1900).');
      return;
    }

    const maxAgeDate = new Date();
    maxAgeDate.setFullYear(maxAgeDate.getFullYear() - 99);
    maxAgeDate.setHours(0, 0, 0, 0);
    
    if (birthDateObj < maxAgeDate) {
      setError('A data de nascimento parece incorreta.');
      return;
    }

    try {
      setSaving(true);

      const userUuid =
        user?.uuid ||
        user?.Uuid ||
        user?.UUID ||
        user?.userUuid ||
        user?.UserUuid ||
        user?.user_uuid ||
        null;

      console.log('[AccountLevelInput] Preparing to save', {
        levelId,
        userUuid,
        user: user ? { id: user.id, email: user.email, hasUuid: !!userUuid } : null,
      });

      if (levelId === 2) {
        if (!userUuid) {
          console.error('[AccountLevelInput] userUuid not found', { user });
          setError('Não foi possível identificar o usuário. Faça login novamente.');
          setSaving(false);
          return;
        }

        const payload = {
          name: trimmedFirst,
          lastName: trimmedLast,
          birthDate: normalizedBirth,
          document: digitsCpf,
          documentType: 1,
        };

        console.log('[AccountLevelInput] Sending level 2 upgrade request', {
          userUuid,
          endpoint: `/user/upgrade/level2/${userUuid}`,
          payload,
        });

        const respostaUpgrade = await api.post(`/user/upgrade/level2/${userUuid}`, payload);

        const updatedUser = {
          ...user,
          name: trimmedFirst,
          lastName: trimmedLast,
          fullName: [trimmedFirst, trimmedLast].filter(Boolean).join(' '),
          cpf: digitsCpf,
          document: digitsCpf,
          taxId: digitsCpf,
          dateBirth: normalizedBirth,
          birthDate: normalizedBirth,
        };

        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setCpf(formatCpf(digitsCpf));
        setBirthDate(normalizedBirth);
        posthog.capture('account_level_submitted', { level: levelId });
        navigate(`/account/level/${levelId}/confirmation`, {
          state: {
            status: respostaUpgrade?.data?.status,
            message: respostaUpgrade?.data?.message,
          },
        });
      } else {
        let currentProfile = {};

        try {
          const currentResponse = await api.get(`/user/id/${user.id}`);
          currentProfile = currentResponse.data || {};
        } catch (fetchError) {
          console.warn(
            'Não foi possível carregar dados atuais do usuário antes da atualização do nível.',
            fetchError
          );
        }

        const safeString = (value) => (typeof value === 'string' ? value : '');
        const existingFirstName =
          trimmedFirst || safeString(currentProfile.name) || safeString(currentProfile.firstName) || '';
        const existingLastName =
          trimmedLast ||
          safeString(currentProfile.lastname || currentProfile.lastName || currentProfile.surname || '');
        const resolvedFullName =
          [existingFirstName, existingLastName].filter(Boolean).join(' ') || safeString(currentProfile.fullName);

        const payload = {
          img: safeString(currentProfile.img),
          name: existingFirstName || resolvedFullName,
          lastname: existingLastName,
          profilename: safeString(currentProfile.profilename),
          nick: safeString(currentProfile.nick),
          email: safeString(currentProfile.email || user?.email),
          personalDescription: safeString(currentProfile.personalDescription),
          document: digitsCpf,
          documentNumber: digitsCpf,
          doc: digitsCpf,
          cpf: digitsCpf,
          taxId: digitsCpf,
          dateBirth: normalizedBirth,
          birthDate: normalizedBirth,
          birthdate: normalizedBirth,
          dateOfBirth: normalizedBirth,
          birthday: normalizedBirth,
          fullName: resolvedFullName,
          firstName: existingFirstName,
          lastName: existingLastName,
        };

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === null) {
            delete payload[key];
          }
        });

        await api.put(`/user/${user.id}`, payload);

        const updatedUser = {
          ...user,
          name: existingFirstName,
          lastName: existingLastName,
          fullName: resolvedFullName,
          cpf: digitsCpf,
          document: digitsCpf,
          taxId: digitsCpf,
          dateBirth: normalizedBirth,
          birthDate: normalizedBirth,
        };

        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        setCpf(formatCpf(digitsCpf));
        setBirthDate(normalizedBirth);
        posthog.capture('account_level_submitted', { level: levelId });
        setSuccess('Informações do nível salvas com sucesso.');
        navigate('/account/level');
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível salvar suas informações.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!Number.isFinite(levelId) || !levelConfig) {
    return (
      <div className="min-h-screen bg-[#f7f7f7] text-slate-700 flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold">Nível inválido</h1>
          <p className="text-sm text-slate-500">
            Não encontramos informações para este nível. Volte para a página de níveis para continuar.
          </p>
          <button
            type="button"
            onClick={() => navigate('/account/level')}
            className="inline-flex items-center gap-2 rounded-full bg-tBlue px-4 py-2 text-sm font-semibold text-white hover:bg-tBlue2 transition-colors"
          >
            Voltar para níveis
            <i className="bi-arrow-right-short text-lg" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list" />
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />

          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate(`/account/level/${levelId}`)}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl" /> Voltar para o nível
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="max-w-4xl mx-auto space-y-8">
            <div className="rounded-3xl border border-amber-200 bg-amber-50 px-6 py-6 shadow-sm">
              <h1 className="text-xl font-semibold">
                {levelConfig.title || `Nível ${levelId}`}
              </h1>
              <p className="mt-2 text-sm text-amber-800">
                Preencha as informações abaixo para concluir este nível.
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-10">
              {loading ? (
                <div className="text-sm text-slate-500">Carregando suas informações...</div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  {error && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                      {success}
                    </div>
                  )}
                  {isLevel2DataComplete && (
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      <i className="bi-info-circle mr-2"></i>
                      Seus dados já foram enviados e estão sendo validados pela nossa equipe. Não é possível alterá-los.
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nome
                      </label>
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <i className="bi-person text-slate-400 text-lg" />
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Seu nome"
                          disabled={isLevel2DataComplete}
                          className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                            isLevel2DataComplete ? 'cursor-not-allowed opacity-60' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Sobrenome
                      </label>
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <i className="bi-person-lines-fill text-slate-300 text-lg" />
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Seu sobrenome"
                          disabled={isLevel2DataComplete}
                          className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                            isLevel2DataComplete ? 'cursor-not-allowed opacity-60' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        CPF
                      </label>
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <i className="bi-card-text text-slate-400 text-lg" />
                        <input
                          type="text"
                          inputMode="numeric"
                          value={cpf}
                          onChange={(e) => setCpf(formatCpf(e.target.value))}
                          placeholder="000.000.000-00"
                          disabled={isLevel2DataComplete}
                          className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                            isLevel2DataComplete ? 'cursor-not-allowed opacity-60' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Data de nascimento
                      </label>
                      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <i className="bi-calendar3 text-slate-400 text-lg" />
                        <input
                          type="date"
                          value={birthDate}
                          min={minBirthDate}
                          max={maxBirthDate}
                          onChange={(e) => setBirthDate(normalizeBirthDate(e.target.value))}
                          placeholder="Data de nascimento"
                          disabled={isLevel2DataComplete}
                          className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                            isLevel2DataComplete ? 'cursor-not-allowed opacity-60' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-slate-500 max-w-xl">
                      Finalize o preenchimento para liberar automaticamente os benefícios do nível{' '}
                      {levelId}.
                    </p>
                    <button
                      type="submit"
                      disabled={
                        isLevel2DataComplete ||
                        saving ||
                        !firstName.trim() ||
                        !lastName.trim() ||
                        cpf.replace(/\D/g, '').length !== 11 ||
                        !isBirthDateValid
                      }
                      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                        isLevel2DataComplete ||
                        saving ||
                        !firstName.trim() ||
                        !lastName.trim() ||
                        cpf.replace(/\D/g, '').length !== 11 ||
                        !isBirthDateValid
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-tBlue text-white hover:bg-tBlue2'
                      }`}
                    >
                      {saving ? 'Salvando...' : isLevel2DataComplete ? 'Dados já enviados' : 'Salvar e continuar'}
                      <i className="bi-arrow-right-short text-lg" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AccountLevelInput;


